import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { SolarProposalProductOfferingDto } from "../dto/solar-proposal-product-offering.dto"

/**
 * Every Solar read a pricing command or margin save can move: the System
 * Pricing step, the investment section's pricing, savings (a cash system's net
 * savings subtract the stored price), contract readiness (its total and
 * freshness blockers), the Share readiness that reads pricing freshness, the
 * header, the Credit Human Quote that pricing writes, the Concert offers
 * Refresh Pricing fetches for the new price, the Sungage application whose
 * pending project and Start gate follow the price, and the LightReach read,
 * whose selected offer is current only under today's pricing. Not awaited, so
 * a command doesn't read as busy while the sections refetch.
 */
export const invalidateSolarPriceDependents = (
  queryClient: QueryClient,
  proposalId: string,
  { except = [] }: { except?: readonly SolarPriceDependent[] } = {},
): void => {
  for (const [dependent, queryKey] of [
    [`systemPricing`, queryKeys.solarProposal.systemPricing(proposalId)],
    [`pricing`, queryKeys.solarProposal.pricing(proposalId)],
    [`savings`, queryKeys.solarProposal.savings(proposalId)],
    [`header`, queryKeys.solarProposal.byId(proposalId)],
    [`contract`, queryKeys.solarProposal.contract(proposalId)],
    [`share`, queryKeys.solarProposal.share(proposalId)],
    [`financing`, queryKeys.solarProposal.financing(proposalId)],
    [`concertOffers`, queryKeys.solarProposal.concertOffers(proposalId)],
    [
      `sungageApplication`,
      queryKeys.solarProposal.sungageApplication(proposalId),
    ],
    [`lightReach`, queryKeys.solarProposal.lightReach(proposalId)],
  ] as const) {
    if (except.includes(dependent)) continue
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
}

export type SolarPriceDependent =
  | `concertOffers`
  | `contract`
  | `financing`
  | `header`
  | `lightReach`
  | `pricing`
  | `savings`
  | `share`
  | `sungageApplication`
  | `systemPricing`

/**
 * The LightReach read and the Summary's `lightReachOffer` requirement: Get
 * LightReach pricing waits on current production and savings, and the
 * stored offers go stale with the design, utility or account LightReach
 * priced. Every command that moves those calls this. Not awaited.
 */
export const invalidateSolarLightReachReaders = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  for (const queryKey of [
    queryKeys.solarProposal.lightReach(proposalId),
    queryKeys.solarProposal.summary(proposalId),
  ]) {
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
}

/**
 * The pricing-input steps a Finance Partner & Equipment or utility save can
 * move: the adder catalog, Protection Plans and VPP programs follow the
 * equipment, finance type and utility; utility rebates follow the post-solar
 * utility, and battery incentives the equipment; rebates belong to the active
 * design. Not awaited.
 */
export const invalidateSolarPricingInputSteps = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  for (const queryKey of [
    queryKeys.solarProposal.adders(proposalId),
    queryKeys.solarProposal.rebates(proposalId),
    queryKeys.solarProposal.extendedWarranties(proposalId),
    queryKeys.solarProposal.incentives(proposalId),
    queryKeys.solarProposal.vppPrograms(proposalId),
  ]) {
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
  // The AI compute nodes PowurOS offers follow the program, Site and utility;
  // the prefix takes the Compute read and its preview.
  void queryClient.invalidateQueries({
    queryKey: queryKeys.solarProposal.compute(proposalId),
  })
  invalidateSolarLightReachReaders(queryClient, proposalId)
}

/**
 * The reads a configuration save leaves stale: the design and equipment show
 * it, pricing and savings report out of date until someone refreshes them on
 * purpose, and the pricing-input steps offer for the new configuration.
 * Nothing here recalculates.
 */
export const refetchAfterProductOfferingSave = async (
  queryClient: QueryClient,
  proposalId: string,
  saved: SolarProposalProductOfferingDto,
): Promise<void> => {
  queryClient.setQueryData(
    queryKeys.solarProposal.productOffering(proposalId),
    saved,
  )
  await Promise.all(
    [
      queryKeys.solarProposal.design(proposalId),
      queryKeys.solarProposal.equipment(proposalId),
      queryKeys.solarProposal.pricing(proposalId),
      queryKeys.solarProposal.systemPricing(proposalId),
      queryKeys.solarProposal.savings(proposalId),
      queryKeys.solarProposal.share(proposalId),
    ].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, exact: true }),
    ),
  )
  // The header, Environmental Impact (home value from the module's wattage),
  // contract readiness (flavor and freshness) and the Credit Human financing
  // read (the plan decides whether a Loan Product applies) aren't awaited.
  for (const queryKey of [
    queryKeys.solarProposal.byId(proposalId),
    // The active version's row shows its equipment and production.
    queryKeys.solarProposal.designVersions(proposalId),
    queryKeys.solarProposal.impact(proposalId),
    queryKeys.solarProposal.contract(proposalId),
    queryKeys.solarProposal.financing(proposalId),
    // A switch to or from Concert decides its offers, seller gate and application.
    queryKeys.solarProposal.concertOffers(proposalId),
    queryKeys.solarProposal.concertSellerReadiness(proposalId),
    queryKeys.solarProposal.concertApplication(proposalId),
    // A Sungage plan or group change decides whether Start replaces the
    // current application and whether it can still be resumed.
    queryKeys.solarProposal.sungageApplication(proposalId),
  ]) {
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
  invalidateSolarPricingInputSteps(queryClient, proposalId)
}
